import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { useParams } from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PhotoCamera from '@material-ui/icons/PhotoCamera';
import axiosInstance from '../services/axiosInstance';
import Navbar from './Navbar';

const useStyles = makeStyles((theme) => ({
  '@global': {
    '@import': [
      'url(https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Manrope:wght@400;500;600;700;800&display=swap)'
    ],
  },
  page: {
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: "'Inter', sans-serif",
    paddingBottom: theme.spacing(6),
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(236, 72, 153, 0.03) 0%, transparent 50%)',
      pointerEvents: 'none',
      zIndex: 0
    }
  },
  header: {
    padding: theme.spacing(5, 4, 3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    position: 'relative',
    zIndex: 1,
  },
  headerTitle: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 800,
    fontSize: '2.5rem',
    background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.03em',
  },
  subHeader: {
    color: '#64748b',
    fontSize: '1.05rem',
    fontWeight: 500,
    lineHeight: 1.7,
    maxWidth: '800px',
  },
  section: {
    margin: theme.spacing(3, 4),
    position: 'relative',
    zIndex: 1,
  },
  card: {
    borderRadius: 16,
    background: 'white',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    }
  },
  formTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    color: '#1e293b',
    marginBottom: theme.spacing(2.5),
    fontWeight: 700,
    fontSize: '1.25rem',
    '& svg': {
      color: '#6366f1',
      fontSize: '1.5rem'
    }
  },
  inputRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    '& .MuiTextField-root': {
      flex: 1,
      minWidth: 200,
    }
  },
  imageInput: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  uploadButton: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '10px',
    padding: theme.spacing(1, 2.5),
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    color: '#0284c7',
    border: '1px solid #bae6fd',
    '&:hover': {
      background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
      borderColor: '#7dd3fc',
    }
  },
  productCard: {
    height: '100%',
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 28px rgba(0, 0, 0, 0.1)',
      borderColor: '#cbd5e1',
    }
  },
  selectedProduct: {
    borderColor: '#6366f1 !important',
    borderWidth: '2px',
    boxShadow: '0 8px 24px rgba(99, 102, 241, 0.2)',
  },
  productMedia: {
    height: 180,
    borderBottom: '1px solid #f1f5f9',
  },
  productContent: {
    padding: theme.spacing(2),
  },
  productTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: '#0f172a',
    marginBottom: theme.spacing(0.5),
  },
  productDescription: {
    color: '#64748b',
    fontSize: '0.9rem',
    marginBottom: theme.spacing(1),
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  productActions: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2, 2),
    gap: theme.spacing(1),
  },
  chip: {
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    color: '#92400e',
    fontWeight: 700,
    border: '1px solid #fcd34d',
    fontSize: '0.875rem',
  },
  chipSecondary: {
    background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
    color: '#7c3aed',
    fontWeight: 600,
    border: '1px solid #c4b5fd',
  },
  muted: {
    color: '#64748b',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      background: '#f8fafc',
      '& fieldset': {
        borderColor: '#e2e8f0',
      },
      '&:hover fieldset': {
        borderColor: '#cbd5e1',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#6366f1',
        borderWidth: '2px',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#64748b',
      '&.Mui-focused': {
        color: '#6366f1',
      },
    },
    '& .MuiOutlinedInput-input': {
      color: '#0f172a',
    }
  },
  primaryButton: {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '10px',
    padding: theme.spacing(1.2, 2.5),
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: 'white',
    border: 'none',
    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.25)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(99, 102, 241, 0.35)',
      background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
    }
  },
  updateButton: {
    flex: 1,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.3)',
    }
  },
  deleteButton: {
    flex: 1,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '10px',
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    '&:hover': {
      background: '#fee2e2',
      borderColor: '#fca5a5',
      transform: 'translateY(-2px)',
    }
  },
  divider: {
    backgroundColor: '#e2e8f0',
    margin: theme.spacing(0, 4),
  },
  sectionTitle: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: '1.85rem',
    color: '#1e293b',
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: '#94a3b8',
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#e2e8f0',
    '& .MuiLinearProgress-bar': {
      background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
      borderRadius: 4,
    }
  },
  groupBar: {
    border: '2px solid #ef4444',
    borderRadius: 10,
    padding: theme.spacing(1.5),
    background: '#f8fafc',
    marginBottom: theme.spacing(1.5),
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  },
  groupBarOrdered: {
    borderColor: '#fb923c',
    boxShadow: '0 6px 14px rgba(251, 146, 60, 0.12)',
  },
  groupBarDelivered: {
    borderColor: '#22c55e',
    boxShadow: '0 6px 14px rgba(34, 197, 94, 0.12)',
  },
  groupBarLocked: {
    borderColor: '#e2e8f0',
  },
  groupBarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  statusChip: {
    backgroundColor: '#fee2e2',
    color: '#b91c1c',
    fontWeight: 700,
  },
  statusChipOrdered: {
    backgroundColor: '#ffedd5',
    color: '#c2410c',
    fontWeight: 700,
  },
  statusChipDelivered: {
    backgroundColor: '#dcfce7',
    color: '#166534',
    fontWeight: 700,
  },
  modalContent: {
    minWidth: 420,
  },
  buyerList: {
    background: '#f8fafc',
    borderRadius: 10,
    marginTop: theme.spacing(2),
    border: '1px solid #e2e8f0',
  },
  switchControl: {
    marginTop: theme.spacing(1),
    '& .MuiFormControlLabel-label': {
      color: '#334155',
      fontWeight: 500,
    },
    '& .MuiSwitch-colorPrimary.Mui-checked': {
      color: '#6366f1',
    },
    '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#6366f1',
    }
  },
  imagePreview: {
    borderRadius: 12,
    marginTop: 12,
    maxHeight: 220,
    objectFit: 'cover',
    border: '1px solid #e2e8f0',
  }
}));

const imageBaseUrl = 'https://backend.csibon.app/static/container_images/';

function DisplayProducts() {
  const classes = useStyles();
  const { establishmentId } = useParams();

  const [products, setProducts] = useState([]);
  const [establishmentProducts, setEstablishmentProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [deliveryThreshold, setDeliveryThreshold] = useState('');
  const [progressEnabled, setProgressEnabled] = useState(true);
  const [currentOrders, setCurrentOrders] = useState('');

  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState('');
  const [newStock, setNewStock] = useState('');
  const [newDeliveryThreshold, setNewDeliveryThreshold] = useState('');
  const [newProgressEnabled, setNewProgressEnabled] = useState(true);
  const [newCurrentOrders, setNewCurrentOrders] = useState('');

  const [productEdits, setProductEdits] = useState({});
  const [groupedOrders, setGroupedOrders] = useState({});
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [orderDate, setOrderDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (establishmentId) {
      fetchEstablishmentProducts();
    }
  }, [establishmentId]);

  const fetchProducts = () => {
    axiosInstance
      .get('/shop/products')
      .then((response) => setProducts(response.data || []))
      .catch((error) => console.error('Error fetching products:', error));
  };

  const fetchEstablishmentProducts = () => {
    axiosInstance
      .get(`/shop/establishment/${establishmentId}/products`)
      .then((response) => {
        const products = response.data || [];
        setEstablishmentProducts(products);
        products.forEach((product) => fetchGroupedOrders(product.id));
      })
      .catch((error) => console.error('Error fetching establishment products:', error));
  };

  const fetchGroupedOrders = (productId) => {
    if (!productId) return;
    axiosInstance
      .get(`/shop/establishment/${establishmentId}/products/${productId}/grouped-progress`)
      .then((response) => setGroupedOrders((prev) => ({ ...prev, [productId]: response.data || {} })))
      .catch((error) => console.error('Error fetching grouped orders:', error));
  };

  const resetNewProductForm = () => {
    setNewName('');
    setNewDescription('');
    setNewPrice('');
    setNewImage(null);
    setNewImagePreview('');
    setNewStock('');
    setNewDeliveryThreshold('');
    setNewProgressEnabled(true);
    setNewCurrentOrders('');
  };

  const handleCreateProduct = () => {
    if (!newName.trim()) {
      alert('Merci de renseigner un nom pour le produit.');
      return;
    }

    const formData = new FormData();
    formData.append('name', newName);
    formData.append('description', newDescription);
    if (newPrice !== '') {
      formData.append('price', newPrice);
    }
    if (newImage) {
      formData.append('file', newImage);
    }

    axiosInstance
      .post('/shop/products', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((response) => {
        const createdProduct = response.data?.container || response.data;
        if (!createdProduct) {
          throw new Error('Produit non créé');
        }
        const payload = {
          establishment_id: establishmentId,
          productshop_id: createdProduct.id,
          nb_stock: newStock || 0,
          is_hidden: false,
          delivery_threshold: newDeliveryThreshold || 0,
          progress_enabled: newProgressEnabled,
          current_orders: newCurrentOrders || 0,
        };
        return axiosInstance.post('/shop/establishment/add_product', payload).then(() => createdProduct.id);
      })
      .then(() => {
        resetNewProductForm();
        fetchProducts();
        fetchEstablishmentProducts();
      })
      .catch((error) => console.error("Erreur lors de l'ajout du produit:", error));
  };

  const handleExistingSubmit = (event) => {
    event.preventDefault();
    if (!selectedProductId) {
      alert('Merci de sélectionner un produit à ajouter.');
      return;
    }

    const payload = {
      establishment_id: establishmentId,
      productshop_id: selectedProductId,
      nb_stock: quantity || 0,
      is_hidden: false,
      delivery_threshold: deliveryThreshold || 0,
      progress_enabled: progressEnabled,
      current_orders: currentOrders || 0,
    };

    axiosInstance
      .post('/shop/establishment/add_product', payload)
      .then(() => {
        setQuantity('');
        setDeliveryThreshold('');
        setCurrentOrders('');
        setSelectedProductId(null);
        setProgressEnabled(true);
        fetchEstablishmentProducts();
      })
      .catch((error) => console.error("Erreur lors de l'ajout du produit existant:", error));
  };

  const handleEditChange = (productId, key, value) => {
    setProductEdits((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], [key]: value },
    }));
  };

  const handleSubmitChanges = (productId) => {
    const updates = productEdits[productId];
    if (!updates) return;

    axiosInstance
      .put(`/shop/establishment/${establishmentId}/products/${productId}`, updates)
      .then(() => fetchEstablishmentProducts())
      .catch((error) => console.error('Erreur lors de la modification du produit:', error));
  };

  const handleDeleteProduct = (productId) => {
    if (!window.confirm('Supprimer ce produit de cet établissement ?')) {
      return;
    }
    axiosInstance
      .delete(`/shop/establishment/${establishmentId}/products/${productId}`)
      .then(() => fetchEstablishmentProducts())
      .catch((error) => console.error('Erreur lors de la suppression du produit:', error));
  };

  const handleOpenGroupModal = (product, group) => {
    if (!group?.is_full) return;
    setSelectedGroup({ product, group });
    const initialDate = group?.supplier_order?.ordered_at
      ? group.supplier_order.ordered_at.slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    setOrderDate(initialDate);
    const initialDeliveryDate = group?.supplier_order?.delivered_at
      ? group.supplier_order.delivered_at.slice(0, 10)
      : '';
    setDeliveryDate(initialDeliveryDate);
    setGroupModalOpen(true);
  };

  const handleCloseGroupModal = () => {
    setGroupModalOpen(false);
    setSelectedGroup(null);
    setOrderDate('');
    setDeliveryDate('');
  };

  const handleMarkSupplierOrdered = () => {
    if (!selectedGroup?.group?.supplier_order?.id) return;

    const payload = {
      status: 'ORDERED',
      ordered_at: orderDate ? new Date(orderDate).toISOString() : new Date().toISOString(),
    };

    axiosInstance
      .put(
        `/shop/establishment/${establishmentId}/products/${selectedGroup.product.id}/supplier-orders/${selectedGroup.group.supplier_order.id}`,
        payload
      )
      .then(() => {
        handleCloseGroupModal();
        fetchGroupedOrders(selectedGroup.product.id);
      })
      .catch((error) => console.error('Erreur lors de la mise à jour de la commande groupée:', error));
  };

  const handleMarkSupplierDelivered = () => {
    if (!selectedGroup?.group?.supplier_order?.id) return;

    const payload = {
      status: 'DELIVERED',
      delivered_at: deliveryDate ? new Date(deliveryDate).toISOString() : new Date().toISOString(),
    };

    axiosInstance
      .put(
        `/shop/establishment/${establishmentId}/products/${selectedGroup.product.id}/supplier-orders/${selectedGroup.group.supplier_order.id}`,
        payload
      )
      .then(() => {
        handleCloseGroupModal();
        fetchGroupedOrders(selectedGroup.product.id);
      })
      .catch((error) => console.error('Erreur lors du passage en livré:', error));
  };

  const productPreview = useMemo(() => {
    if (newImagePreview) return newImagePreview;
    return '';
  }, [newImagePreview]);

  return (
    <div className={classes.page}>
      <Navbar />
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>
          Gestion boutique établissement
        </Typography>
        <Typography className={classes.subHeader}>
          Ajoutez des produits avec leur photo, ajustez les stocks et définissez le seuil de livraison groupée pour chaque point de vente.
        </Typography>
      </div>

      <div className={classes.section}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <div className={classes.formTitle}>
                  <AddIcon />
                  <Typography variant="h6">Créer un nouveau produit</Typography>
                </div>
                <div className={classes.inputRow}>
                  <TextField
                    label="Nom"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                  <TextField
                    label="Prix"
                    type="number"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    variant="outlined"
                    margin="dense"
                    style={{ minWidth: 160 }}
                    className={classes.textField}
                  />
                </div>
                <TextField
                  label="Description"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  margin="dense"
                  className={classes.textField}
                />
                <div className={classes.imageInput}>
                  <Button
                    variant="outlined"
                    className={classes.uploadButton}
                    component="label"
                    startIcon={<PhotoCamera />}
                  >
                    Choisir une image
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        setNewImage(file || null);
                        setNewImagePreview(file ? URL.createObjectURL(file) : '');
                      }}
                    />
                  </Button>
                  {newImage && <Chip label={newImage.name} className={classes.chipSecondary} />}
                </div>
                {productPreview && (
                  <CardMedia
                    component="img"
                    image={productPreview}
                    className={classes.imagePreview}
                  />
                )}
                <Divider style={{ margin: '20px 0', backgroundColor: '#e2e8f0' }} />
                <div className={classes.inputRow}>
                  <TextField
                    label="Stock initial"
                    type="number"
                    value={newStock}
                    onChange={(e) => setNewStock(e.target.value)}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                  <TextField
                    label="Seuil de livraison groupée"
                    type="number"
                    value={newDeliveryThreshold}
                    onChange={(e) => setNewDeliveryThreshold(e.target.value)}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                  <TextField
                    label="Commandes en cours"
                    type="number"
                    value={newCurrentOrders}
                    onChange={(e) => setNewCurrentOrders(e.target.value)}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                </div>
                <FormControlLabel
                  className={classes.switchControl}
                  control={
                    <Switch
                      checked={newProgressEnabled}
                      onChange={(e) => setNewProgressEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Afficher la barre de progression"
                />
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button variant="contained" className={classes.primaryButton} onClick={handleCreateProduct}>
                    Enregistrer et ajouter
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <div className={classes.formTitle}>
                  <CheckCircleIcon />
                  <Typography variant="h6">Ajouter un produit existant</Typography>
                </div>
                <form onSubmit={handleExistingSubmit}>
                  <div className={classes.inputRow}>
                    <TextField
                      label="Stock"
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                    <TextField
                      label="Seuil de livraison groupée"
                      type="number"
                      value={deliveryThreshold}
                      onChange={(e) => setDeliveryThreshold(e.target.value)}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                    <TextField
                      label="Commandes en cours"
                      type="number"
                      value={currentOrders}
                      onChange={(e) => setCurrentOrders(e.target.value)}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                  </div>
                  <FormControlLabel
                    className={classes.switchControl}
                    control={
                      <Switch
                        checked={progressEnabled}
                        onChange={(e) => setProgressEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Afficher la barre de progression"
                  />
                  <Typography variant="body2" className={classes.muted} style={{ marginTop: 12, marginBottom: 12 }}>
                    Sélectionnez un produit ci-dessous pour le lier à cet établissement.
                  </Typography>
                  <Grid container spacing={2}>
                    {products.map((product) => (
                      <Grid item xs={12} sm={6} key={product.id}>
                        <Card
                          className={`${classes.productCard} ${selectedProductId === product.id ? classes.selectedProduct : ''}`}
                          onClick={() => setSelectedProductId(product.id)}
                        >
                          <CardMedia
                            className={classes.productMedia}
                            image={product.image_url ? `${imageBaseUrl}${product.image_url}` : 'https://placehold.co/300x180?text=Image'}
                            title={product.name}
                          />
                          <CardContent className={classes.productContent}>
                            <Typography className={classes.productTitle}>
                              {product.name}
                            </Typography>
                            <Typography className={classes.productDescription}>
                              {product.description}
                            </Typography>
                            <Chip label={`${product.price ?? 0} €`} size="small" className={classes.chip} />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button type="submit" variant="contained" className={classes.primaryButton}>
                      Ajouter au point de vente
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>

      <Divider className={classes.divider} style={{ marginTop: 32, marginBottom: 32 }} />

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Produits de cet établissement
        </Typography>
        {establishmentProducts.length === 0 ? (
          <Paper className={classes.card}>
            <Box className={classes.emptyState}>
              <Typography className={classes.muted}>
                Aucun produit n'est encore associé à ce point de vente.
              </Typography>
            </Box>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {establishmentProducts.map((product) => {
              const edited = productEdits[product.id] || {};
              const progressValue = edited.current_orders ?? product.current_orders ?? 0;
              const thresholdValue = edited.delivery_threshold ?? product.delivery_threshold ?? 0;
              const progressPercent = thresholdValue > 0 ? Math.min(100, (progressValue / thresholdValue) * 100) : 0;
              const groupedInfo = groupedOrders[product.id];
              const groupEntries = groupedInfo?.groups || [];
              return (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <Card className={classes.productCard} style={{ cursor: 'default' }}>
                    <CardMedia
                      className={classes.productMedia}
                      image={product.image_url ? `${imageBaseUrl}${product.image_url}` : 'https://placehold.co/300x180?text=Image'}
                      title={product.name}
                    />
                    <CardContent className={classes.productContent}>
                      <Typography className={classes.productTitle}>{product.name}</Typography>
                      <Typography className={classes.productDescription} style={{ WebkitLineClamp: 3 }}>
                        {product.description}
                      </Typography>
                      <Chip label={`${product.price ?? 0} €`} size="small" className={classes.chip} />
                      {(edited.progress_enabled ?? product.progress_enabled) && thresholdValue > 0 && (
                        <Box mt={2}>
                          <Typography variant="body2" className={classes.muted} style={{ marginBottom: 6 }}>
                            Commandes groupées (seuil : {thresholdValue})
                          </Typography>
                          {groupEntries.length > 0 ? (
                            groupEntries.map((group) => {
                              const percent = thresholdValue > 0 ? Math.min(100, (group.orders_count / thresholdValue) * 100) : 0;
                              const status = (group.supplier_order?.status || 'PENDING').toUpperCase();
                              const statusLabel =
                                status === 'DELIVERED'
                                  ? 'Livré'
                                  : status === 'ORDERED'
                                  ? 'Commandé'
                                  : 'En attente';
                              const chipClass =
                                status === 'DELIVERED'
                                  ? classes.statusChipDelivered
                                  : status === 'ORDERED'
                                  ? classes.statusChipOrdered
                                  : classes.statusChip;
                              const barClasses = `${classes.groupBar} ${
                                status === 'DELIVERED'
                                  ? classes.groupBarDelivered
                                  : status === 'ORDERED'
                                  ? classes.groupBarOrdered
                                  : ''
                              } ${!group.is_full ? classes.groupBarLocked : ''}`;
                              return (
                                <Box
                                  key={`${product.id}-group-${group.group_index}`}
                                  className={barClasses}
                                  onClick={group.is_full ? () => handleOpenGroupModal(product, group) : undefined}
                                  style={{ cursor: group.is_full ? 'pointer' : 'default' }}
                                >
                                  <div className={classes.groupBarHeader}>
                                    <Typography variant="body2" className={classes.muted}>
                                      Commande groupée #{group.group_index}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={statusLabel}
                                      className={chipClass}
                                    />
                                  </div>
                                  <LinearProgress variant="determinate" value={percent} className={classes.progressBar} />
                                  <Typography variant="caption" className={classes.muted}>
                                    {group.orders_count} / {thresholdValue} acheteurs
                                  </Typography>
                                </Box>
                              );
                            })
                          ) : (
                            <>
                              <Typography variant="body2" className={classes.muted} style={{ marginBottom: 6 }}>
                                Commandes : {progressValue} / {thresholdValue}
                              </Typography>
                              <LinearProgress variant="determinate" value={progressPercent} className={classes.progressBar} />
                            </>
                          )}
                        </Box>
                      )}
                      <TextField
                        label="Stock"
                        type="number"
                        value={edited.nb_stock ?? product.nb_stock ?? 0}
                        onChange={(e) => handleEditChange(product.id, 'nb_stock', e.target.value)}
                        fullWidth
                        margin="dense"
                        className={classes.textField}
                      />
                      <TextField
                        label="Seuil de livraison groupée"
                        type="number"
                        value={thresholdValue}
                        onChange={(e) => handleEditChange(product.id, 'delivery_threshold', e.target.value)}
                        fullWidth
                        margin="dense"
                        className={classes.textField}
                      />
                      <TextField
                        label="Commandes comptabilisées"
                        type="number"
                        value={progressValue}
                        onChange={(e) => handleEditChange(product.id, 'current_orders', e.target.value)}
                        fullWidth
                        margin="dense"
                        className={classes.textField}
                      />
                      <FormControlLabel
                        className={classes.switchControl}
                        control={
                          <Switch
                            checked={edited.progress_enabled ?? product.progress_enabled ?? false}
                            onChange={(e) => handleEditChange(product.id, 'progress_enabled', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Afficher la progression"
                      />
                      <FormControlLabel
                        className={classes.switchControl}
                        control={
                          <Switch
                            checked={edited.is_hidden ?? product.is_hidden}
                            onChange={(e) => handleEditChange(product.id, 'is_hidden', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Masquer le produit"
                      />
                    </CardContent>
                    <CardActions className={classes.productActions}>
                      <Button variant="contained" className={classes.updateButton} onClick={() => handleSubmitChanges(product.id)}>
                        Mettre à jour
                      </Button>
                      <Button variant="outlined" className={classes.deleteButton} onClick={() => handleDeleteProduct(product.id)}>
                        Supprimer
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </div>

      <Dialog open={groupModalOpen} onClose={handleCloseGroupModal} fullWidth maxWidth="sm">
        <DialogTitle>
          Commande groupée #{selectedGroup?.group?.group_index || ''}
        </DialogTitle>
        <DialogContent className={classes.modalContent}>
          <Typography variant="body1" style={{ fontWeight: 600, marginBottom: 8 }}>
            {selectedGroup?.product?.name}
          </Typography>
          <Typography variant="body2" className={classes.muted}>
            Créée le : {selectedGroup?.group?.supplier_order?.created_at ? new Date(selectedGroup.group.supplier_order.created_at).toLocaleDateString() : 'Non renseignée'}
          </Typography>
          <TextField
            label="Date de commande fournisseur"
            type="date"
            fullWidth
            margin="normal"
            value={orderDate}
            onChange={(e) => setOrderDate(e.target.value)}
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Date de livraison"
            type="date"
            fullWidth
            margin="normal"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="subtitle2" style={{ marginTop: 8 }}>
            Acheteurs ({selectedGroup?.group?.buyers?.length || 0})
          </Typography>
          <List className={classes.buyerList} dense>
            {(selectedGroup?.group?.buyers || []).map((buyer, index) => {
              const displayName = `${buyer.first_name || ''} ${buyer.last_name || buyer.pseudo || ''}`.trim() || 'Acheteur';
              return (
                <ListItem key={`${buyer.id || 'buyer'}-${index}`}>
                  <ListItemText primary={displayName} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGroupModal}>Fermer</Button>
          <Button onClick={handleMarkSupplierOrdered} color="primary" variant="contained">
            Marquer comme commandé
          </Button>
          <Button onClick={handleMarkSupplierDelivered} color="secondary" variant="contained" disabled={!selectedGroup?.group?.supplier_order}>
            Marquer comme livré
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DisplayProducts;